import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Field } from 'app/shared/components/field/model/field.interface';
import { FormDefinition, FormSubscription, UserForm } from 'app/shared/models/class/forms/form-definition';
import { FullOffer } from 'app/shared/models/class/offer/offer';
import { AuthService } from 'app/shared/services/auth/auth.service';
import { FaiconService } from 'app/shared/services/faicon/faicon.service';
import { FormService } from 'app/shared/services/forms/form.service';
import { OfferService } from 'app/shared/services/offer/offer.service';
import { UiService } from 'app/shared/services/ui/ui.service';
import { SubscriptionBaseComponent } from 'app/shared/subscriptionBase/subscription-base';


@Component({
  selector: 'app-admin-subscription-form',
  templateUrl: './admin-subscription-form.component.html',
  styleUrls: ['./admin-subscription-form.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AdminSubscriptionFormComponent extends SubscriptionBaseComponent implements OnInit {

  form!: UntypedFormGroup;
  isLoading: boolean = false;
  fields: Field[] = [];
  formDefinitionKey!: number;
  formEdited!: FormSubscription;
  actorCode!: string;
  articles: FullOffer[] = [];
  articleKey!: number;
  formDefinitions: FormDefinition[] = [];
  formSelected!: FormDefinition;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly uiService: UiService,
    private readonly authService: AuthService,
    private readonly faIconService: FaiconService,
    private readonly formService: FormService,
    private readonly route: ActivatedRoute,
    private readonly offerService: OfferService,
    private readonly router: Router
  )
  {
    super();
    this.actorCode = this.authService.getDecodedToken().club_code;
  }

  ngOnInit(): void {
    this.initForm();
    this.checkParameters();
  }

  getFaIcon(faIcon: string): string {
    return this.faIconService.getFaIcon(faIcon);
  }

  onSelectFormDefinition(event: any): void {
    this.formSelected = event.value;
  }

  back(): void {
    void this.router.navigate(['/admin/subscriptions'], { queryParams: { articleKey: this.articleKey } });
  }

  onFieldsChange(fields: Field[]): void {
    this.fields = fields;
  }

  save(): void {
    this.isLoading = true;
    const formValue = this.form.value;

    let userForm: UserForm = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      participantCount: formValue.participantCount,
      city: formValue.city,
      email: formValue.email,
      phoneNumber: formValue.phoneNumber
    };

    let subscriptionToSend: FormSubscription = {
      articleKey: this.articleKey,
      formDefinitionKey: this.formSelected.key!,
      user: userForm,
      fields: this.fields
    };

    if (this.formEdited) {
      subscriptionToSend.key = this.formEdited.key;
      this.formService.putFormSubscription(subscriptionToSend).subscribe({
        next: () => {
          this.uiService.showSnackbar(`La souscription a été mise à jour avec succès.`);
          this.isLoading = false;
          void this.router.navigate(['/admin/subscriptions'], { queryParams: { articleKey: this.articleKey } });

        },
        error: (err: any) => {
          console.error('Error updating form subscription:', err);
          this.isLoading = false;
        }
      });

    } else {
      this.formService.postFormSubscription(subscriptionToSend).subscribe({
        next: () => {
          this.uiService.showSnackbar(`La souscription a été ajoutée avec succès.`);
          this.isLoading = false;
          void this.router.navigate(['/admin/subscriptions'], { queryParams: { articleKey: this.articleKey } });
        },
        error: (err: any) => {
          console.error('Error creating form subscription:', err);
          this.isLoading = false;
        }
      });
    }

  }

  private checkParameters(): void {
    this.subscription.add(
      this.route.queryParams.subscribe(params => {
        this.articleKey = Number.parseInt(params['articleKey']);
        if (params['key']) {
          const key = Number.parseInt(params['key']);
          this.formService.getFormSubscriptionByKey(key).subscribe({
            next: (formSubscription: FormSubscription) => {
              this.formEdited = formSubscription;
              this.form.patchValue({
                lastName: this.formEdited.user.lastName,
                firstName: this.formEdited.user.firstName,
                participantCount: this.formEdited.user.participantCount,
                city: this.formEdited.user.city,
                email: this.formEdited.user.email,
                phoneNumber: this.formEdited.user.phoneNumber,
              });
              this.init();
            },
            error: (err) => {
              console.error('Error fetching form subscription by key:', err);
            }
          })
        } else {
          this.init();
        }

      })
    );
  }

  private init(): void {
    this.formService.getFormDefinition().subscribe({
      next: formDefinitions => {
        this.formDefinitions = formDefinitions.filter(fd => fd.enabled);
        if (this.formEdited) {
          const formDef = this.formDefinitions.find(fd => fd.key === this.formEdited.formDefinitionKey);
          this.form.get('formDefinition')?.setValue(formDef);
          this.formSelected = formDef!;
        }
      },
      error: err => {
        console.error('Error fetching form definitions:', err);
      }
    });

    this.offerService.getFullOffer(this.actorCode).subscribe({
      next: (fullOffer: FullOffer[]) => {
        this.articles = fullOffer.filter(offer => offer.isActive);
        const article = this.articles.find(art => art.offerKey === this.articleKey);
        const articleToDisplay = article ? `${article.offerKey} - ${article.offerName}` : '';
        this.form.get('article')?.setValue(articleToDisplay);
        this.form.get('article')?.updateValueAndValidity();
        if (this.formEdited) {
          this.fields = this.formEdited.fields;
        }
      },
      error: (err) => {
        console.error('Error fetching full offer:', err);
      }
    });
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      lastName: ['', Validators.required],
      firstName: ['', Validators.required],
      participantCount: [1, Validators.required],
      city: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', Validators.required],
      article: [{value: '', disabled: true}],
      formDefinition: [null, Validators.required]
    });
  }

}
